/**
 * The signing page's stage, per challenge.
 *
 * The route reuses one page for every /members/step-up/:challengeId, so a
 * stage is held with the challenge it belongs to, and a ceremony's result
 * counts only for the challenge it was started for. Without this, opening a
 * new challenge after one was signed or expired showed the old result, and a
 * prompt still open for the old one could land on the new page.
 *
 * Pure, so the hand-over from one challenge to the next is unit tested.
 */
import type { StepUpStatus } from "state/members/stepUp";

import type { StepUpStage } from "./StepUpView";

export const REVIEW: StepUpStage = { name: "review" };

/** Review, adding a passkey on this device first (the portal's "Add one here"). */
export const ADD_HERE: StepUpStage = { name: "review", adding: true };

export type HeldStage = {
  challengeId: string | undefined;
  stage: StepUpStage;
};

/**
 * The stage to show for this challenge; one held for another starts over, at
 * `start` (where the visit asked to begin: review, or adding a passkey here).
 */
export const stageFor = (
  held: HeldStage,
  challengeId: string | undefined,
  start: StepUpStage = REVIEW,
): StepUpStage => (held.challengeId === challengeId ? held.stage : start);

/**
 * Starting (true) or leaving (false) adding a passkey on this device, from
 * review. Leaving keeps a way back (`offerAdd`), so a member who opened at it
 * from "Add one here" and pressed Cancel need not fail a prompt to return.
 */
export const addingHere = (stage: StepUpStage, adding: boolean): StepUpStage =>
  stage.name !== "review"
    ? stage
    : adding
      ? { ...stage, adding: true }
      : { ...stage, adding: false, offerAdd: true };

/**
 * What the page shows for a stage once the challenge's own status is known: a
 * review of a challenge that can no longer be signed shows why. A ceremony in
 * flight waits for its own outcome: a signature marks the cached challenge
 * consumed as it lands, and the page can render that before its "signed".
 */
export const withChallengeStatus = (
  stage: StepUpStage,
  status: StepUpStatus,
): StepUpStage => {
  if (stage.name !== "review") return stage;
  if (status === "expired") return { name: "expired" };
  if (status === "consumed" || status === "cancelled") return { name: "used" };
  return stage;
};

/**
 * Which challenge is on screen, and which ones have a ceremony running. Read
 * and written outside React state, so a double click and a late result are
 * both caught before the next render.
 */
export const createCeremonyTracker = () => {
  let shown: string | undefined;
  const signing = new Set<string>();

  return {
    show: (challengeId: string | undefined) => {
      shown = challengeId;
    },
    /** Starts a ceremony; false when this challenge already has one running. */
    begin: (challengeId: string): boolean => {
      if (signing.has(challengeId)) return false;
      signing.add(challengeId);
      return true;
    },
    end: (challengeId: string) => {
      signing.delete(challengeId);
    },
    /** Whether a result for this challenge still belongs on screen. */
    isShown: (challengeId: string): boolean => challengeId === shown,
  };
};
